import { Injectable } from '@angular/core';
import { Observable, Subject, forkJoin } from 'rxjs';
import { takeUntil, map, catchError } from 'rxjs/operators';
import { AvaliacaoService, ItemAvaliavel, RegistroAvaliacao } from '../../../core/config/services/avaliacao.service';
import { NotaService, Periodo, ItemEducacional } from './nota.service';
import { ContextService } from '../../../core/context/services/context.service';
import { MobileContextService, ContextoPWA } from '../../../core/context/services/mobile-context.service';
import { parseJSONSafe } from '../../../shared/utils';
import { STORAGE_KEYS, LEGACY_STORAGE_KEYS } from '../../../shared/constants';
import { of } from 'rxjs';

export interface Turma {
  id: number;
  descricao: string;
  tipoAtendimento: string;
  separacaoAlunosEmGrupos: boolean;
  etapaMatriz: {
    modular: boolean;
  };
  tipoResultado?: string;
  /** Quantidade de exames finais configurados para a turma (0 = sem exame final) */
  quantidadeExamesFinais?: number;
}

@Injectable({
  providedIn: 'root'
})
export class DesempenhoDataService {
  constructor(
    private avaliacaoService: AvaliacaoService,
    private notaService: NotaService,
    private contextService: ContextService,
    private mobileContextService: MobileContextService
  ) {}

  /**
   * Carrega turma do contexto. `ContextService.getTurma()` já prioriza
   * `MobileContextService` internamente — não precisa checar os dois aqui.
   */
  loadTurmaFromContext(): Turma | null {
    const turmaContext = this.contextService.getTurma();
    if (turmaContext?.id) {
      return turmaContext as Turma;
    }

    return this.loadTurmaFromBridge();
  }

  private loadTurmaFromBridge(): Turma | null {
    // Fallback de leitura na chave antiga (__pwa_bridge_contexto_completo) por 1 release — TODO remover
    const contextoBridge = localStorage.getItem(STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO) ||
      localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO);
    if (!contextoBridge) {
      return null;
    }

    const contexto = parseJSONSafe<ContextoPWA | null>(contextoBridge, null);
    if (contexto?.turma?.id) {
      this.mobileContextService.atualizarContexto({
        servidor: contexto.servidor,
        anoLetivo: contexto.anoLetivo,
        estabelecimento: contexto.estabelecimento,
        turma: contexto.turma,
        entidade: contexto.entidade,
        componentes: contexto.componentes
      }, true);
      return contexto.turma as Turma;
    }

    return null;
  }

  loadPeriodoFromContext(): Periodo | null {
    const periodoFromService = this.contextService.getPeriodo();
    return periodoFromService ? {
      ...periodoFromService,
      descricao: periodoFromService.descricao || '',
      dataInicial: periodoFromService.dataInicial || '',
      dataFinal: periodoFromService.dataFinal || ''
    } : null;
  }

  loadItensEducacionaisFromContext(): ItemEducacional[] {
    const componentesFromService = this.contextService.getComponentes();
    return componentesFromService.map((c: { id: number; descricao?: string; nome?: string; color?: string }) => ({
      id: c.id,
      descricao: c.descricao || c.nome || '',
      isCampoExperiencia: false,
      color: c.color
    }));
  }

  loadItensEducacionaisFromAPI(turmaId: number, destroy$: Subject<void>): Observable<ItemEducacional[]> {
    return this.avaliacaoService.getItensEducacionais(turmaId)
      .pipe(
        takeUntil(destroy$),
        map((itens: Array<{ id: number; descricao?: string; nome?: string; isCampoExperiencia?: boolean; color?: string }> | null | undefined) => {
          if (itens && Array.isArray(itens) && itens.length > 0) {
            return itens.map((item: { id: number; descricao?: string; nome?: string; isCampoExperiencia?: boolean; color?: string }) => ({
              id: item.id,
              descricao: (item.descricao || item.nome) || '',
              isCampoExperiencia: item.isCampoExperiencia || false,
              color: item.color
            }));
          }
          return [];
        })
      );
  }

  loadPeriodos(turmaId: number, destroy$: Subject<void>): Observable<Periodo[]> {
    return this.avaliacaoService.getPeriodosAvaliativos(turmaId)
      .pipe(takeUntil(destroy$));
  }

  loadEnturmacoes(
    turmaId: number,
    searchKeywords: string,
    destroy$: Subject<void>
  ): Observable<unknown[]> {
    let filter = '';
    if (searchKeywords) {
      filter = `nome like "${searchKeywords}"`;
    }

    return this.avaliacaoService.getEnturmacoesHistoricas(turmaId, filter, 0, 999)
      .pipe(
        takeUntil(destroy$),
        map(data => Array.isArray(data) ? data : [])
      );
  }

  loadEncerramentos(turmaId: number, destroy$: Subject<void>): Observable<unknown[]> {
    return this.avaliacaoService.getEncerramentosByTurma(turmaId)
      .pipe(takeUntil(destroy$));
  }

  loadEnturmacoesAndEncerramentos(
    turmaId: number,
    searchKeywords: string,
    destroy$: Subject<void>
  ): Observable<{ enturmacoes: unknown[]; encerramentos: unknown[] }> {
    return forkJoin({
      enturmacoes: this.loadEnturmacoes(turmaId, searchKeywords, destroy$),
      encerramentos: this.loadEncerramentos(turmaId, destroy$)
    });
  }

  loadAvaliacoesPorInstrumento(
    turmaId: number,
    periodoId: number | undefined,
    itemEducacionalId: number,
    destroy$: Subject<void>
  ): Observable<{ itensAvaliaveis?: ItemAvaliavel[]; registrosAvaliacao?: RegistroAvaliacao[]; registros?: RegistroAvaliacao[] }> {
    return this.avaliacaoService.listAvaliacoes(turmaId, periodoId, itemEducacionalId, undefined)
      .pipe(
        takeUntil(destroy$),
        catchError(() => of({ itensAvaliaveis: [], registrosAvaliacao: [], registros: [] }))
      );
  }

  loadAvaliacoesCompletos(
    turmaId: number,
    itemEducacionalId: number,
    destroy$: Subject<void>
  ): Observable<{ content?: RegistroAvaliacao[] }> {
    const filter = `(itemAvaliacaoTurma.itemEducacional in (${itemEducacionalId}))`;
    return this.avaliacaoService.listAvaliacoesCompletos(turmaId, filter)
      .pipe(takeUntil(destroy$));
  }

  loadItensAvaliaveisForYearView(
    turmaId: number,
    itemEducacionalId: number,
    destroy$: Subject<void>
  ): Observable<{ content?: ItemAvaliavel[] }> {
    const filter = `tipoPeriodo not in ("INSTRUMENTO", "CALCULO_INSTRUMENTOS", "CHA_OBJETIVO_MEDIA_PERIODO", "CHA_OBJETIVO_EXAME_FINAL") and itemEducacional=${itemEducacionalId}`;
    return this.avaliacaoService.listItensAvaliaveis(turmaId, filter)
      .pipe(takeUntil(destroy$));
  }

  loadStatusCalculo(
    turmaId: number,
    itemEducacionalId: number,
    periodoId: number,
    destroy$: Subject<void>
  ): Observable<{ calculo?: unknown; formulaCalculo?: unknown }> {
    return this.avaliacaoService.statusCalculo(turmaId, itemEducacionalId, periodoId)
      .pipe(takeUntil(destroy$));
  }

  loadFormulasCalculo(turmaId: string, destroy$: Subject<void>): Observable<{ content: unknown[]; totalElements: number }> {
    return this.avaliacaoService.getFormulasCalculo(undefined, 0, 999)
      .pipe(takeUntil(destroy$));
  }

  filterItensAvaliaveisPorInstrumento(itensAvaliaveis: ItemAvaliavel[]): ItemAvaliavel[] {
    return itensAvaliaveis.filter((item: ItemAvaliavel) => 
      item.tipoPeriodo === 'INSTRUMENTO' || item.tipoPeriodo === 'CALCULO_INSTRUMENTOS'
    );
  }

  buildFilterListAvaliaveis(itemEducacionalId: number): string {
    return `tipoPeriodo not in ("INSTRUMENTO", "CALCULO_INSTRUMENTOS", "CHA_OBJETIVO_MEDIA_PERIODO", "CHA_OBJETIVO_EXAME_FINAL") and itemEducacional=${itemEducacionalId}`;
  }
}

